import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Edit, Factory, Package } from 'lucide-react';
import useAuth from 'auth/auth';
import {
  FacilitySummary,
  Manufacturer,
} from 'services/api/domains/manufacturers/types';
import { Facility } from 'services/api/domains/facilities/types';
import { useManufacturersPage } from '../services/api/domains/manufacturers/hooks';
import {
  useMergeManufacturersSuggestions,
  useMergeManufacturersPreview,
  useMergeManufacturers,
  useSearchManufacturers,
} from '../services/api/domains/manufacturers/hooks';
import GenericMergeModal from '../components/modals/GenericMergeModal';
import { useUpdateFacility } from '../services/api/domains/facilities/hooks';
import { FacilityService } from '../services/api/domains/facilities/service';
import { useApi } from '../services/api/provider';
import ManufacturerModal, {
  ModalMode,
} from '../components/modals/ManufacturerModal';
import FacilityModal from '../components/modals/FacilityModal';
import ManufacturerNameGateNotice, {
  describeProductNameActions,
  getOrphanRefusal,
  getManufacturerDeleteOrphanRefusal,
  getProductsRemovalOrphanRefusal,
  getManufacturerRoleNameRemovalRefusal,
} from '../components/ManufacturerNameGateNotice';
import {
  UnifiedSearchBar,
  UnifiedSearchState,
} from 'components/UnifiedSearchBar';
import {
  createEmptyAdvancedQuery,
  SearchRule,
} from 'components/TableSearch/types';
import { manufacturersFieldConfig } from '../config/searchConfigs';
import { MANUFACTURER_PRESETS } from '../domains/manufacturers/filterPresets';
import {
  STORAGE_KEY_MFR_FILTER_ACTIVE,
  STORAGE_KEY_MFR_FILTER_PRESETS,
  STORAGE_KEY_MFR_GRID_FILTER,
} from 'utils/storageKeys';
import LgmDataGrid from 'components/LgmDataGrid';
import LgmButton from 'components/LgmButton';
import { ManufacturerOverflowMenu } from 'components/ManufacturerOverflowMenu';
import { useTableDensity } from 'shared/hooks/useTableDensity';
import { useSessionStorageState } from 'shared/hooks/useSessionStorageState';
import {
  toDataGridColumns,
  getDefaultColumnVisibility,
} from '../domains/manufacturers/adapters';
import {
  GridColDef,
  GridFilterModel,
  GridSortModel,
  GridPaginationModel,
  GridColumnVisibilityModel,
} from '@mui/x-data-grid-premium';
import {
  gridFilterToAdvancedQuery,
  mergeAdvancedQueries,
} from 'utils/dataGridHelpers';
import { GRAY_100, GRAY_200, GRAY_300 } from 'theme/grayScale';
import type { AdvancedSearchQuery } from 'components/TableSearch/types';
import {
  COLOR_SUCCESS_TEXT,
  COLOR_WARNING_TEXT,
  COLOR_INFO,
  COLOR_INFO_TEXT,
} from 'theme/colors';

const PAGE_SIZE = 50;

interface ManufacturerReviewProps {
  /** Deep-link filter from the Products view (Task 2.3) — seeds advanced search on mount. */
  initialManufacturerId?: number;
  /** Display name for the id above, shown in the advanced-search summary instead of the raw id. */
  initialManufacturerName?: string;
  /** Deep-link filter from the Metrics view — an arbitrary AdvancedSearchQuery
   * (e.g. product_count > 0), not a single manufacturer id. Takes priority
   * over initialManufacturerId/-Name when both are somehow present. */
  initialManufacturersFilter?: AdvancedSearchQuery | null;
  /** Called once the initial filter above has been applied, so the parent can clear its pending state. */
  onInitialFilterConsumed?: () => void;
  /** Boot-time/Back-Forward `?search=<term>` basic-search deep link (bug
   * fix, 2026-09-29) -- sibling to initialManufacturersFilter above but for
   * the plain-text search box rather than an AdvancedSearchQuery. */
  initialManufacturersSearchTerm?: string | null;
  /** Called once the initial search term above has been applied. */
  onInitialSearchTermConsumed?: () => void;
  /** Called on every search/filter change (basic term or advanced query),
   * so the parent can keep the address bar's `?filter=`/`?search=` in sync
   * with whatever is actually applied -- not just the moment a Metrics-card
   * deep link is clicked (bug fix, 2026-09-29). */
  onSearchStateChange?: (state: UnifiedSearchState) => void;
  /** True whenever this mount was forced by an explicit URL-driven
   * navigation, so an all-null initial filter/search resolves to an
   * explicit "clear" override instead of falling back to
   * UnifiedSearchBar's own sessionStorage-restored state (Copilot review,
   * PR #840 round 3). */
  forceSearchStateOverride?: boolean;
  /** Opens the Products view filtered by the given query (Task 3,
   * generalized in metrics-deep-linking MDL-006 from a hardcoded
   * manufacturer_id to an arbitrary AdvancedSearchQuery). */
  onViewProducts?: (query: AdvancedSearchQuery) => void;
}

const ManufacturerReview: React.FC<ManufacturerReviewProps> = ({
  initialManufacturerId,
  initialManufacturerName,
  initialManufacturersFilter,
  onInitialFilterConsumed,
  initialManufacturersSearchTerm,
  onInitialSearchTermConsumed,
  onSearchStateChange,
  forceSearchStateOverride,
  onViewProducts,
}) => {
  const { isViewer, isAdmin } = useAuth();
  const { density } = useTableDensity();
  const rowHeight = density === 'compact' ? 36 : 52;

  // Captures the deep-link id at mount time only (Task 4.2). The live
  // `initialManufacturerId` prop is cleared by the parent (via
  // `onInitialFilterConsumed`, below) almost immediately after mount --
  // long before the async manufacturers fetch this page kicks off has a
  // chance to resolve. An effect that depended on the live prop would see
  // it become `undefined` first and never get to check the fetched data.
  const initialManufacturerIdRef = useRef(initialManufacturerId);

  // Search state — managed by UnifiedSearchBar, reflected here for API params.
  // Lazy-initialized from `initialManufacturersFilter` or `initialManufacturerId`
  // (Task 2.3) when present -- REPLACES the default empty/basic state entirely
  // (not merged), same replace-not-merge decision as
  // ProductsRowGroupingDataGrid's Task 2.2.
  const [searchState, setSearchState] = useState<UnifiedSearchState>(() => {
    if (initialManufacturersFilter) {
      return {
        mode: 'advanced',
        basicTerm: '',
        advancedQuery: initialManufacturersFilter,
      };
    }
    if (initialManufacturerId !== undefined) {
      const rule: SearchRule = {
        id: 'initial-manufacturer-filter',
        field: 'manufacturer_id',
        operator: 'equals',
        value: initialManufacturerId,
        label: initialManufacturerName,
      };
      return {
        mode: 'advanced',
        basicTerm: '',
        advancedQuery: {
          filterGroups: [
            {
              id: 'initial-manufacturer-filter-group',
              logicalOperator: 'AND',
              rules: [rule],
            },
          ],
          groupOperator: 'AND',
        },
      };
    }
    if (initialManufacturersSearchTerm) {
      return {
        mode: 'basic',
        basicTerm: initialManufacturersSearchTerm,
        advancedQuery: createEmptyAdvancedQuery(),
      };
    }
    return {
      mode: 'basic',
      basicTerm: '',
      advancedQuery: createEmptyAdvancedQuery(),
    };
  });

  // Tell the parent the initial filter has been consumed -- see the matching
  // comment in ProductsRowGroupingDataGrid.tsx (Task 2.2) for why real deps
  // are listed here instead of a mount-only suppression.
  useEffect(() => {
    if (initialManufacturerId !== undefined || initialManufacturersFilter) {
      onInitialFilterConsumed?.();
    }
  }, [
    initialManufacturerId,
    initialManufacturersFilter,
    onInitialFilterConsumed,
  ]);

  // Mirrors the effect above for the `?search=<term>` sibling slot.
  useEffect(() => {
    if (initialManufacturersSearchTerm) {
      onInitialSearchTermConsumed?.();
    }
  }, [initialManufacturersSearchTerm, onInitialSearchTermConsumed]);

  // DataGrid state
  const [gridFilterModel, setGridFilterModel] =
    useSessionStorageState<GridFilterModel>(STORAGE_KEY_MFR_GRID_FILTER, {
      items: [],
    });
  const [gridSortModel, setGridSortModel] = useState<GridSortModel>([]);
  const [gridPaginationModel, setGridPaginationModel] =
    useState<GridPaginationModel>({
      page: 0,
      pageSize: PAGE_SIZE,
    });
  const [columnVisibilityModel, setColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>(getDefaultColumnVisibility());

  const manufacturersParams = useMemo(() => {
    let finalAdvancedQuery = createEmptyAdvancedQuery();

    // Start with UnifiedSearchBar query (basic or advanced)
    if (searchState.mode === 'advanced') {
      finalAdvancedQuery = searchState.advancedQuery;
    }

    if (gridFilterModel.items.length > 0) {
      const gridQuery = gridFilterToAdvancedQuery(
        gridFilterModel,
        manufacturersFieldConfig
      );

      if (gridQuery.filterGroups.length > 0) {
        finalAdvancedQuery = mergeAdvancedQueries(
          finalAdvancedQuery,
          gridQuery
        );
      }
    }

    // Check if there are actual filter rules, not just empty groups
    const hasActualFilters = finalAdvancedQuery.filterGroups.some(
      group => group.rules && group.rules.length > 0
    );

    const params = {
      search:
        searchState.mode === 'basic'
          ? searchState.basicTerm.trim() || undefined
          : undefined,
      advancedSearch: hasActualFilters ? finalAdvancedQuery : undefined,
      sortBy:
        gridSortModel.length > 0
          ? (gridSortModel[0].field as keyof Manufacturer)
          : undefined,
      sortDirection:
        gridSortModel.length > 0
          ? (gridSortModel[0].sort?.toUpperCase() as 'ASC' | 'DESC')
          : undefined,
      limit: gridPaginationModel.pageSize,
      offset: gridPaginationModel.page * gridPaginationModel.pageSize,
    };

    return params;
  }, [searchState, gridFilterModel, gridSortModel, gridPaginationModel]);

  // Same filter/sort as manufacturersParams, minus pagination -- the
  // overflow menu's Excel export (Sub-task 11.3) pages through every
  // matching manufacturer itself rather than exporting only this page.
  const exportFilterParams = useMemo(
    () => ({
      search: manufacturersParams.search,
      advancedSearch: manufacturersParams.advancedSearch,
      sortBy: manufacturersParams.sortBy,
      sortDirection: manufacturersParams.sortDirection,
    }),
    [manufacturersParams]
  );

  const {
    update: updateManufacturer,
    delete: deleteManufacturer,
    data: manufacturersData,
    total,
    isLoading: isManufacturersLoading,
    refetch: refetchManufacturers,
    manufacturers: manufacturersQuery,
  } = useManufacturersPage(manufacturersParams);

  // Merge modal's target picker searches across every manufacturer, not just
  // this page's `manufacturersData` (issue #653 Task 22).
  const searchManufacturers = useSearchManufacturers();

  // Preserve total during loading to prevent DataGrid from resetting pagination. The hook reports 0 while
  // a request is in flight, so only a settled response updates it -- including a real total of 0, or an
  // empty search or filter result would keep the previous total in the footer.
  const [stableTotal, setStableTotal] = useState(total || 0);
  useEffect(() => {
    if (manufacturersQuery.status === 'success') {
      setStableTotal(total ?? 0);
    }
  }, [total, manufacturersQuery.status]);

  // Modal state for ManufacturerModal
  const [manufacturerModalMode, setManufacturerModalMode] =
    useState<ModalMode | null>(null);
  const [selectedManufacturer, setSelectedManufacturer] =
    useState<Manufacturer | null>(null);

  // Seq 9.2 (PIV-020): see ProductsReview.tsx's identical notice for the
  // steady-state manufacturer-name gate's moved/created notice and Q29
  // would-orphan refusal.
  const [manufacturerNameGateNotice, setManufacturerNameGateNotice] = useState<{
    messages: string[];
    variant: 'info' | 'danger';
  } | null>(null);

  // Facilities modal
  const [openFacilities, setOpenFacilities] = useState<Manufacturer | null>(
    null
  );

  // FacilityModal state
  const [facilityModalMode, setFacilityModalMode] = useState<ModalMode | null>(
    null
  );
  const [selectedFacility, setSelectedFacility] = useState<Facility | null>(
    null
  );

  // Merge modal state
  const [openMergeModal, setOpenMergeModal] = useState(false);
  const [mergeSourceManufacturer, setMergeSourceManufacturer] =
    useState<Manufacturer | null>(null);

  // The View Facilities window lists the facilities linked to the open manufacturer (the grid row's own
  // `facilities` summaries: the Manufacturer_Facilities links, archived owners' facilities hidden). It used
  // to look each facility up in the general facilities list, which only holds the first page, so makers
  // whose facilities fell outside it showed an empty window. It reads the CURRENT row (not the row captured
  // when the window opened) so a refetch after a facility edit, un-archive or merge refreshes the window.
  const openManufacturerRow = openFacilities
    ? (manufacturersData?.find(
        m => m.manufacturer_id === openFacilities.manufacturer_id
      ) ?? openFacilities)
    : null;
  const rowFacilities = openManufacturerRow?.facilities;
  const manufacturerFacilities: FacilitySummary[] = Array.isArray(rowFacilities)
    ? rowFacilities
    : [];

  const updateFacilityMutation = useUpdateFacility();
  const api = useApi();
  const facilityService = useMemo(() => new FacilityService(api), [api]);

  // Identifies the facility load in flight. Closing the window, opening another manufacturer or asking for a
  // second facility supersedes it, and a superseded request must neither open the editor nor alert.
  const editRequestRef = useRef(0);
  const closeFacilitiesWindow = useCallback(() => {
    editRequestRef.current += 1;
    setOpenFacilities(null);
  }, []);

  // A successful refresh that no longer returns the open manufacturer (for example a search by facility name
  // after that facility was renamed) would leave the window on its opening snapshot indefinitely: close it.
  // While the rows are loading the list is empty, so the window is kept.
  useEffect(() => {
    if (
      openFacilities &&
      manufacturersQuery.status === 'success' &&
      !manufacturersData?.some(
        m => m.manufacturer_id === openFacilities.manufacturer_id
      )
    ) {
      closeFacilitiesWindow();
    }
  }, [
    openFacilities,
    manufacturersQuery.status,
    manufacturersData,
    closeFacilitiesWindow,
  ]);

  // Editing needs the full facility record, which the row's summary does not carry: fetch just that one.
  const handleEditFacility = async (facilityId: number) => {
    const requestId = ++editRequestRef.current;
    try {
      const response = await facilityService.getById(facilityId);
      if (requestId !== editRequestRef.current) return;
      setSelectedFacility(response.data);
      setFacilityModalMode('EDIT');
    } catch (err) {
      if (requestId !== editRequestRef.current) return;
      console.error('❌ Load facility failed:', err);
      alert('❌ Failed to load the facility. Please try again.');
    }
  };

  // Auto-open the manufacturer's detail modal once a deep-linked filter
  // (Task 2.3) resolves — same "View" code path as the manual Info action,
  // no separate modal-opening logic (Task 4.2). Fires once, on the first
  // load to settle after mount; guarded so a later refetch (e.g. after
  // editing a facility) never reopens it. If the filter matches zero rows
  // (manufacturer deleted) or fails to load, `match` is undefined and the
  // grid just shows its normal state -- no modal is force-opened.
  //
  // `useQuery`'s state starts as `{ status: 'idle' }` before its fetch
  // effect has run, so a derived `isLoading` flag (`status === 'loading'`)
  // is *false* on the very first render too -- indistinguishable from
  // "fetch settled, zero rows" if checked directly. Reading the raw
  // `status` and only treating `'success'`/`'error'` as settled (not just
  // "not currently loading") is correct even if a future cache-hit path
  // resolves straight from `'idle'` to `'success'` without ever passing
  // through `'loading'`.
  const isManufacturersQuerySettled =
    manufacturersQuery.status === 'success' ||
    manufacturersQuery.status === 'error';

  const autoOpenAttemptedRef = useRef(false);
  useEffect(() => {
    const targetId = initialManufacturerIdRef.current;
    if (
      targetId === undefined ||
      autoOpenAttemptedRef.current ||
      !isManufacturersQuerySettled
    ) {
      return;
    }
    autoOpenAttemptedRef.current = true;
    const match = (manufacturersData || []).find(
      m => m.manufacturer_id === targetId
    );
    if (match) {
      setSelectedManufacturer(match);
      setManufacturerModalMode('VIEW');
    }
  }, [isManufacturersQuerySettled, manufacturersData]);

  // DataGrid columns from adapter
  const manufacturerColumns = useMemo<GridColDef<Manufacturer>[]>(
    () => toDataGridColumns(),
    []
  );

  // Reset DataGrid page when search changes. Bails out (returns the same
  // `prev` reference) when the page is already 0 -- otherwise this fires on
  // every `searchState` change, including UnifiedSearchBar's mount-time sync
  // of restored session state, handing `manufacturersParams` a new
  // `gridPaginationModel` object it can't tell apart from a real change and
  // aborting the in-flight manufacturers fetch for no reason (issue #679).
  useEffect(() => {
    setGridPaginationModel(prev =>
      prev.page === 0 ? prev : { ...prev, page: 0 }
    );
  }, [searchState]);

  const handleSearchChange = useCallback(
    (state: UnifiedSearchState) => {
      setSearchState(state);
      // Keeps the address bar in sync with whatever is actually applied,
      // not just the moment a Metrics-card deep link is clicked (bug fix,
      // 2026-09-29).
      onSearchStateChange?.(state);
    },
    [onSearchStateChange]
  );

  const handleDelete = async (id: number) => {
    if (
      !window.confirm(
        'Delete manufacturer and all related facilities/products?'
      )
    )
      return;
    try {
      await deleteManufacturer.mutate(id);
      refetchManufacturers();
    } catch (err) {
      // Seq 14.1 (PIV-008) orphan refusal -- nothing was deleted; surface
      // the backend's message instead of failing silently.
      const orphanRefusal = getManufacturerDeleteOrphanRefusal(err);
      if (orphanRefusal) {
        setManufacturerNameGateNotice({
          messages: [orphanRefusal.error],
          variant: 'danger',
        });
        return;
      }
      console.error('❌ Delete failed:', err);
      alert('❌ Failed to delete manufacturer. Please try again.');
    }
  };

  const loadFacilities = (m: Manufacturer) => {
    // Opening another manufacturer supersedes any facility load still in flight for the previous one.
    editRequestRef.current += 1;
    setOpenFacilities(m);
  };

  // New unified modal handlers
  const handleSaveManufacturer = async (manufacturerData: Manufacturer) => {
    // Clear any notice from a previous save -- otherwise a stale moved/
    // created or refusal message from an earlier edit keeps showing as if
    // it applies to this one.
    setManufacturerNameGateNotice(null);
    try {
      // A Names-tab-only save (ManufacturerModal.tsx) never populates
      // `.facilities` at all — only the Facilities panel does. Omitting
      // `facility_ids` here (instead of defaulting to []) matters:
      // updateManufacturer() treats an empty array as "sync to zero
      // facilities," so defaulting silently removed every real facility
      // link on every Names-only edit.
      const payload = Array.isArray(manufacturerData.facilities)
        ? {
            ...manufacturerData,
            facility_ids: manufacturerData.facilities.map(f => f.facility_id),
          }
        : manufacturerData;
      const result = await updateManufacturer.mutate(payload);
      if (result.manufacturerNameActions?.length) {
        setManufacturerNameGateNotice({
          messages: describeProductNameActions(result.manufacturerNameActions),
          variant: 'info',
        });
      }
      setManufacturerModalMode(null);
      setSelectedManufacturer(null);
      refetchManufacturers();
    } catch (err) {
      // Q29 would-orphan refusal (Seq 9.1, PIV-020) — nothing was written;
      // keep the modal open rather than silently closing on a save that
      // didn't actually apply.
      const orphanRefusal = getOrphanRefusal(err);
      if (orphanRefusal) {
        setManufacturerNameGateNotice({
          messages: [orphanRefusal.error],
          variant: 'danger',
        });
        return;
      }
      // Seq 14.1 (PIV-008) Products-panel removal refusal (PR #877 review)
      // -- editing this manufacturer's own product list down to zero
      // manufacturers for one of them is refused; keep the modal open with
      // the backend's message instead of silently closing/console-erroring.
      const productsRemovalRefusal = getProductsRemovalOrphanRefusal(err);
      if (productsRemovalRefusal) {
        setManufacturerNameGateNotice({
          messages: [productsRemovalRefusal.error],
          variant: 'danger',
        });
        return;
      }
      // tr_ProductNames_ManufacturerRoleInvariant trigger-rollback refusal
      // (PR #877 review) — the Products-panel orphan pre-check above only
      // counts remaining manufacturer LINKS, not remaining active
      // manufacturer-role NAMES, so this is the backstop for removing this
      // manufacturer from a product that keeps another, unnamed link.
      const roleNameRefusal = getManufacturerRoleNameRemovalRefusal(err);
      if (roleNameRefusal) {
        setManufacturerNameGateNotice({
          messages: [roleNameRefusal.error],
          variant: 'danger',
        });
        return;
      }
      console.error('❌ Save manufacturer failed:', err);
    }
  };

  const handleCloseManufacturerModal = () => {
    setManufacturerModalMode(null);
    setSelectedManufacturer(null);
  };

  // New unified facility modal handlers
  const handleSaveFacility = async (facilityData: Facility) => {
    try {
      await updateFacilityMutation.mutate(facilityData);
      setFacilityModalMode(null);
      setSelectedFacility(null);
      // Refreshes the grid rows, which the open View Facilities window reads from.
      refetchManufacturers();
    } catch (err) {
      console.error('❌ Save facility failed:', err);
    }
  };

  return (
    <div style={styles.pageContainerStyle}>
      {manufacturerNameGateNotice && (
        <ManufacturerNameGateNotice
          messages={manufacturerNameGateNotice.messages}
          variant={manufacturerNameGateNotice.variant}
          onDismiss={() => setManufacturerNameGateNotice(null)}
        />
      )}
      <div
        style={styles.searchBarContainerStyle}
        data-layout-role="search-panel">
        <UnifiedSearchBar
          fieldConfigs={manufacturersFieldConfig}
          placeholder="Search manufacturers..."
          builtInPresets={MANUFACTURER_PRESETS}
          storageKeyUserPresets={STORAGE_KEY_MFR_FILTER_PRESETS}
          storageKeyActivePreset={STORAGE_KEY_MFR_FILTER_ACTIVE}
          defaultPreset={MANUFACTURER_PRESETS.find(p => p.id === 'mfr_all')}
          // forceSearchStateOverride also forces the override when every
          // one of the three above resolved to null/undefined -- a genuine
          // "Back/Forward to a bare URL" resolution, not "nothing to
          // override with" (Copilot review, PR #840 round 3).
          initialSearchState={
            initialManufacturerId !== undefined ||
            initialManufacturersFilter ||
            initialManufacturersSearchTerm ||
            forceSearchStateOverride
              ? searchState
              : undefined
          }
          onSearchChange={handleSearchChange}
          actions={
            <ManufacturerOverflowMenu
              filterParams={exportFilterParams}
              exportDisabled={stableTotal === 0}
            />
          }
        />
      </div>

      <div style={styles.tableContainerStyle} data-layout-role="table-body">
        <LgmDataGrid<Manufacturer>
          data={manufacturersData || []}
          loading={isManufacturersLoading}
          columns={manufacturerColumns}
          rowCount={stableTotal}
          rowHeight={rowHeight}
          paginationModel={gridPaginationModel}
          onPaginationModelChange={setGridPaginationModel}
          sortModel={gridSortModel}
          onSortModelChange={setGridSortModel}
          filterModel={gridFilterModel}
          onFilterModelChange={setGridFilterModel}
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={setColumnVisibilityModel}
          getRowId={row => row.manufacturer_id}
          onInfo={m => {
            setSelectedManufacturer(m);
            setManufacturerModalMode('VIEW');
          }}
          onEdit={
            !isViewer
              ? m => {
                  setSelectedManufacturer(m);
                  setManufacturerModalMode('EDIT');
                }
              : undefined
          }
          onDelete={
            !isViewer ? m => handleDelete(m.manufacturer_id) : undefined
          }
          onMerge={
            isAdmin
              ? m => {
                  setMergeSourceManufacturer(m);
                  setOpenMergeModal(true);
                }
              : undefined
          }
          actions={(_m: Manufacturer) => [
            {
              icon: <Factory size={16} />,
              onAction: (m: Manufacturer) => loadFacilities(m),
              title: 'View Facilities',
              style: styles.facilitiesButtonStyle,
              hoverStyle: {
                color: '#15803d', // hover: darker green (green-700)
              },
              pressedStyle: {
                color: COLOR_SUCCESS_TEXT, // active: one step darker (green-800)
              },
            },
            {
              // View Products deep link (manufacturer-product-linking, Task
              // 3.1) -- mirrors View Facilities exactly: no RBAC gate (read
              // access available to all roles per api/auth.js), same icon
              // action slot shape.
              icon: <Package size={16} />,
              onAction: (m: Manufacturer) =>
                onViewProducts?.({
                  groupOperator: 'AND',
                  filterGroups: [
                    {
                      id: 'manufacturer-filter-group',
                      logicalOperator: 'AND',
                      rules: [
                        {
                          id: 'manufacturer-filter-rule',
                          field: 'manufacturer_id',
                          operator: 'equals',
                          value: m.manufacturer_id,
                          label: m.manufacturer_name,
                        },
                      ],
                    },
                  ],
                }),
              title: 'View Products',
              style: styles.productsButtonStyle,
              hoverStyle: {
                color: COLOR_INFO_TEXT, // hover: darker blue
              },
              pressedStyle: {
                color: COLOR_INFO_TEXT,
              },
            },
          ]}
        />
      </div>

      {/* ManufacturerModal */}
      {manufacturerModalMode && selectedManufacturer && (
        <ManufacturerModal
          manufacturer={selectedManufacturer}
          initialMode={manufacturerModalMode}
          save={handleSaveManufacturer}
          close={handleCloseManufacturerModal}
        />
      )}

      {/* GenericMergeModal for Manufacturers */}
      {openMergeModal && mergeSourceManufacturer && (
        <GenericMergeModal<Manufacturer>
          source={mergeSourceManufacturer}
          allEntities={manufacturersData}
          searchEntities={searchManufacturers}
          config={{
            domainDisplayName: 'Manufacturer',
            domainDisplayNamePlural: 'Manufacturers',
            entityIdField: 'manufacturer_id',
            entityNameField: 'manufacturer_name',
            entityCreatedDateField: 'created_date',
            useMergeSuggestions: useMergeManufacturersSuggestions,
            useMergePreview: useMergeManufacturersPreview,
            useMerge: useMergeManufacturers,
          }}
          onClose={() => {
            setOpenMergeModal(false);
            setMergeSourceManufacturer(null);
          }}
          onSuccess={() => {
            setOpenMergeModal(false);
            setMergeSourceManufacturer(null);
            refetchManufacturers();
          }}
        />
      )}

      {/* Facilities Modal */}
      {openFacilities && (
        <div style={styles.modalOverlayStyle}>
          <div style={styles.facilitiesModalContentStyle}>
            <h2 style={styles.modalTitleStyle}>
              Facilities for {openFacilities.manufacturer_name}
            </h2>
            <table style={styles.facilitiesTableStyle}>
              <thead>
                <tr style={styles.tableHeaderStyle}>
                  <th style={styles.facilitiesTableHeaderCellStyle}>
                    Facility Name
                  </th>
                  <th style={styles.facilitiesTableHeaderCellStyle}>City</th>
                  <th style={styles.facilitiesTableHeaderCellStyle}>State</th>
                  <th style={styles.facilitiesTableHeaderCellStyle}>Country</th>
                  <th style={styles.facilitiesTableHeaderCellStyle}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {(openFacilities ? manufacturerFacilities : []).map(
                  (f: FacilitySummary) => (
                    <tr key={f.facility_id}>
                      <td style={styles.facilitiesTableCellStyle}>
                        {f.facility_name}
                      </td>
                      <td style={styles.facilitiesTableCellStyle}>{f.city}</td>
                      <td style={styles.facilitiesTableCellStyle}>
                        {f.state_province}
                      </td>
                      <td style={styles.facilitiesTableCellStyle}>
                        {f.country_code}
                      </td>
                      <td style={styles.facilitiesTableCellStyle}>
                        <LgmButton
                          variant="flat"
                          onClick={() => handleEditFacility(f.facility_id)}
                          disabled={isViewer}
                          style={styles.buttonEnabled}
                          hoverStyle={{
                            backgroundColor: 'transparent',
                            ...styles.buttonEnabledHover,
                          }}
                          pressedStyle={{
                            backgroundColor: 'transparent',
                            ...styles.buttonEnabledPressed,
                          }}
                          aria-label="Edit"
                          title="Edit">
                          <Edit size={16} />
                        </LgmButton>
                      </td>
                    </tr>
                  )
                )}
              </tbody>
            </table>
            <div style={styles.modalButtonsStyle}>
              <LgmButton
                variant="contained"
                color="secondary"
                onClick={closeFacilitiesWindow}>
                Close
              </LgmButton>
            </div>
          </div>
        </div>
      )}

      {/* Facility Modal */}
      {selectedFacility && (
        <FacilityModal
          facility={selectedFacility}
          initialMode={facilityModalMode || 'VIEW'}
          save={handleSaveFacility}
          close={() => {
            setSelectedFacility(null);
            setFacilityModalMode('VIEW');
          }}
        />
      )}
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  searchUploadContainerStyle: {
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%',
    alignItems: 'flex-start',
  } as React.CSSProperties,
  titleStyle: {
    fontSize: '1.25rem', // text-xl
    fontWeight: 'bold',
    margin: 'auto',
  } as React.CSSProperties,
  tableHeaderStyle: {
    backgroundColor: GRAY_100, // bg-gray-100
  } as React.CSSProperties,
  facilitiesButtonStyle: {
    color: '#16a34a', // text-green-600
    cursor: 'pointer',
    backgroundColor: 'transparent',
    border: 'none',
  } as React.CSSProperties,
  productsButtonStyle: {
    color: COLOR_INFO,
    cursor: 'pointer',
    backgroundColor: 'transparent',
    border: 'none',
  } as React.CSSProperties,
  buttonEnabled: {
    color: '#d97706', // text-yellow-600
    cursor: 'pointer',
    backgroundColor: 'transparent',
    border: 'none',
    padding: '0', // compact icon-action hitbox, matching TableModelCell's pattern
  } as React.CSSProperties,
  buttonEnabledHover: {
    color: COLOR_WARNING_TEXT, // hover:text-yellow-800
  } as React.CSSProperties,
  buttonEnabledPressed: {
    color: '#78350f', // active: one step darker than hover
  } as React.CSSProperties,
  modalOverlayStyle: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    zIndex: 50,
  } as React.CSSProperties,
  facilitiesModalContentStyle: {
    backgroundColor: 'white',
    padding: '1.5rem', // p-6
    borderRadius: '0.5rem', // rounded-lg
    boxShadow:
      '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)', // shadow-lg
    width: '75%', // w-3/4
  } as React.CSSProperties,
  modalTitleStyle: {
    fontSize: '1.125rem', // text-lg
    fontWeight: 'bold',
    marginBottom: '1rem', // mb-4
  } as React.CSSProperties,
  modalButtonsStyle: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: '1rem', // mt-4
  } as React.CSSProperties,
  cancelButtonStyle: {
    backgroundColor: GRAY_300, // bg-gray-300
    padding: '0.5rem 1rem', // px-4 py-2
    borderRadius: '0.25rem', // rounded
    border: 'none',
    cursor: 'pointer',
  } as React.CSSProperties,
  facilitiesTableStyle: {
    width: '100%',
    border: `1px solid ${GRAY_200}`,
  } as React.CSSProperties,
  facilitiesTableHeaderCellStyle: {
    padding: '0.5rem', // p-2
    border: `1px solid ${GRAY_200}`,
    textAlign: 'left',
  } as React.CSSProperties,
  facilitiesTableCellStyle: {
    border: `1px solid ${GRAY_200}`,
    padding: '0.5rem', // p-2
  } as React.CSSProperties,
  mergeButtonStyle: {
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    backgroundColor: 'transparent',
    color: '#8b5cf6', // text-gray-500
    transition: 'color 0.2s',
  } as React.CSSProperties,
  mergeButtonHoverStyle: {
    color: '#6d28d9', // hover:text-purple-600
  } as React.CSSProperties,
  pageContainerStyle: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  } as React.CSSProperties,
  searchBarContainerStyle: {
    flexShrink: 0,
    backgroundColor: '#ffffff',
    borderBottom: `1px solid ${GRAY_300}`,
    paddingBottom: '8px',
  } as React.CSSProperties,
  tableContainerStyle: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    minHeight: 0,
  } as React.CSSProperties,
};

export default ManufacturerReview;
